import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronsUpDown, Check, Settings, LogOut, Activity, Pencil, HelpCircle, Star, Sun, Moon } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { DynamicIcon } from "./DynamicIcon";
import { useSidebarNavigation, FAVORITES_SECTION_ID } from "@/hooks/use-sidebar-navigation";
import { useOrganization } from "@/hooks/use-organization";
import { useUnreadMessageCount } from "@/hooks/use-unread-message-count";
import { SectionNavItem, NavItem } from "@/config/navigation";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePageContext } from "@/hooks/use-page-context";
import { getSectionTips } from "@/config/page-tips";
import { SetupChecklist } from "@/components/onboarding/SetupChecklist";

// Key for persisting sidebar scroll position across route changes
const SIDEBAR_SCROLL_KEY = "sidebar-nav-scroll";

interface SidebarNavigationProps {
  initials?: string | null;
  fullName?: string | null;
  onLogout: () => void;
  onEditProfile: () => void;
}

export function SidebarNavigation({
  initials,
  fullName,
  onLogout,
  onEditProfile,
}: SidebarNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { sections, isFavorite, toggleFavorite } = useSidebarNavigation();
  const { isDark, toggleTheme } = useTheme();
  const { organization, allMemberships, hasMultipleOrgs, switchOrganization } = useOrganization();
  const { totalUnread } = useUnreadMessageCount();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem("sidebar-expanded-sections");
    if (saved) {
      try {
        const parsed = new Set(JSON.parse(saved) as string[]);
        // Always include Favorites
        parsed.add(FAVORITES_SECTION_ID);
        return parsed;
      } catch { /* fall through */ }
    }
    return new Set([FAVORITES_SECTION_ID]);
  });
  const navRef = useRef<HTMLElement>(null);
  const { openSectionTips } = usePageContext();

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (savedScroll && navRef.current) {
      navRef.current.scrollTop = parseInt(savedScroll, 10);
    }
  }, []);

  // Save scroll position before unmount or on scroll
  const handleScroll = useCallback(() => {
    if (navRef.current) {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(navRef.current.scrollTop));
    }
  }, []);

  // Collect all known nav paths to avoid false positives on "starts with" matching
  const allNavPaths = React.useMemo(() => {
    const paths = new Set<string>();
    sections.forEach((section) => {
      section.items.forEach((item) => {
        paths.add(item.path.split("?")[0]);
        item.children?.forEach((child) => {
          paths.add(child.path.split("?")[0]);
        });
      });
    });
    return paths;
  }, [sections]);

  // Check if a path is active
  const isActive = (path: string) => {
    const [itemPath, itemQuery] = path.split("?");
    const currentPath = location.pathname;
    const currentSearch = location.search;

    // Exact path match
    if (currentPath === itemPath) {
      if (itemQuery) {
        return currentSearch === `?${itemQuery}`;
      }
      return true;
    }

    // Nested route match (e.g., /clients/:id matches /clients)
    // But NOT if the current path is another known nav item (e.g., /clients/forecast)
    if (itemPath !== "/" && currentPath.startsWith(itemPath + "/")) {
      // Don't match if current path is a known nav item
      if (!allNavPaths.has(currentPath)) {
        return true;
      }
    }

    return false;
  };

  // Check if any child is active
  const hasActiveChild = (item: SectionNavItem) => {
    return item.children?.some((child) => isActive(child.path)) ?? false;
  };

  // Persist expanded sections to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "sidebar-expanded-sections",
      JSON.stringify([...expandedSections])
    );
  }, [expandedSections]);

  // Auto-expand section containing the active route — ONLY when section isn't
  // already open. Do NOT collapse other sections; users explicitly open/close them.
  // Deps: pathname/search only — re-running on `sections` reference changes
  // (which fires on every subscription/flag re-render) causes flicker when the
  // user tries to open a different section.
  useEffect(() => {
    let needsUpdate = false;
    const nextExpanded = new Set(expandedSections);

    for (const section of sections) {
      if (section.id === FAVORITES_SECTION_ID) continue;
      const sectionHasActiveItem = section.items.some(
        (item) => isActive(item.path) || hasActiveChild(item)
      );
      if (sectionHasActiveItem && !nextExpanded.has(section.id)) {
        nextExpanded.add(section.id);
        needsUpdate = true;
      }
    }
    if (needsUpdate) setExpandedSections(nextExpanded);

    // Auto-expand sub-items with active children
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children && hasActiveChild(item)) {
          setExpandedItems((prev) => {
            if (prev.has(item.id)) return prev;
            const next = new Set(prev);
            next.add(item.id);
            return next;
          });
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Accordion toggle: expanding one section collapses others (Favorites always open)
  const toggleSection = (sectionId: string) => {
    if (sectionId === FAVORITES_SECTION_ID) return; // Can't collapse Favorites
    setExpandedSections((prev) => {
      const next = new Set([FAVORITES_SECTION_ID]);
      if (!prev.has(sectionId)) next.add(sectionId);
      return next;
    });
  };

  const toggleExpand = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleItemClick = (item: SectionNavItem) => {
    if (item.children && item.children.length > 0) {
      toggleExpand(item.id);
      if (!hasActiveChild(item) && !isActive(item.path)) {
        navigate(item.children[0].path);
      }
    } else {
      navigate(item.path);
    }
  };

  const handleChildClick = (child: NavItem) => {
    navigate(child.path);
  };

  return (
    <div className="flex flex-col h-full bg-background border-r border-border/40 w-64">
      {/* Org Switcher / Logo */}
      <div className="flex items-center h-16 px-4 border-b border-border/40 shrink-0">
        {hasMultipleOrgs ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 w-full rounded-lg hover:bg-muted/60 px-1 py-1.5 transition-colors">
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0 text-primary-foreground font-display font-semibold text-sm tracking-tight">
                  {organization?.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <span className="font-display text-[15px] font-semibold text-foreground truncate flex-1 text-left tracking-tight">
                  {organization?.name || "Select Org"}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {allMemberships.map((m) => (
                <DropdownMenuItem
                  key={m.organization_id}
                  onClick={() => {
                    if (m.organization_id !== organization?.id) {
                      switchOrganization(m.organization_id);
                      navigate("/dashboard");
                    }
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <div className="h-7 w-7 rounded-lg bg-primary flex items-center justify-center shrink-0 text-primary-foreground font-semibold text-xs">
                    {m.organization.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.organization.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{m.org_role}</p>
                  </div>
                  {m.organization_id === organization?.id && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0 text-primary-foreground font-display font-semibold text-sm tracking-tight">
              {organization?.name?.charAt(0)?.toUpperCase() || <Activity className="h-4 w-4" />}
            </div>
            <span className="font-display text-[15px] font-semibold text-foreground truncate tracking-tight">
              {organization?.name || "TrainerPulse"}
            </span>
          </div>
        )}
      </div>

      {/* Navigation Sections */}
      <nav
        ref={navRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto py-4 px-3 space-y-6"
      >
        {sections.map((section) => {
          const isFavoritesSection = section.id === FAVORITES_SECTION_ID;
          // Check if this section has tips configured
          const sectionHasTips = !!getSectionTips(section.id);

          const isSectionExpanded = expandedSections.has(section.id);

          return (
          <div key={section.id}>
            {/* Section Header */}
            {isFavoritesSection ? (
              // Favorites header: not clickable, no chevron, always open
              <div className="flex items-center justify-between w-full px-3 mb-2">
                <h3 className="eyebrow text-muted-foreground/70">
                  {section.label}
                </h3>
              </div>
            ) : (
              // Regular section header: clickable with chevron
              <button
                onClick={() => toggleSection(section.id)}
                className="flex items-center justify-between w-full px-3 mb-2 group"
              >
                <h3 className="eyebrow text-muted-foreground/70">
                  {section.label}
                </h3>
                <div className="flex items-center gap-1">
                  {sectionHasTips && isSectionExpanded && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openSectionTips(section.id); }}
                      className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted transition-colors"
                      title={`Learn about ${section.label}`}
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 text-muted-foreground/50 transition-transform",
                      isSectionExpanded && "rotate-180"
                    )}
                  />
                </div>
              </button>
            )}

            {/* Section Items */}
            {isSectionExpanded && (
            <div className="space-y-1">
              {section.items.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.id);
                const itemIsActive = isActive(item.path) || hasActiveChild(item);
                const starred = isFavorite(item.id);
                const isPermanent = !!item.permanent;
                // In Favorites section: show filled star on non-permanent items (click to unfavorite)
                // In other sections: show star on hover (click to favorite/unfavorite)
                const showStarInFavorites = isFavoritesSection && !isPermanent;
                const showStarInOther = !isFavoritesSection;

                return (
                  <div key={`${section.id}-${item.id}`} className="group/item relative">
                    {itemIsActive && !hasChildren && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-sm bg-primary"
                      />
                    )}
                    <button
                      onClick={() => handleItemClick(item)}
                      className={cn(
                        "flex items-center w-full rounded-lg transition-colors duration-150 gap-3 pl-4 pr-3 py-2",
                        itemIsActive && !hasChildren
                          ? "bg-primary/[0.06] text-foreground font-semibold"
                          : itemIsActive && hasChildren
                          ? "text-foreground bg-muted/50"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      )}
                    >
                      <DynamicIcon
                        name={item.icon}
                        className={cn(
                          "h-4 w-4 shrink-0 transition-colors duration-200",
                          itemIsActive && !hasChildren && "text-primary"
                        )}
                      />
                      <span className="flex-1 text-left text-[13px] font-medium tracking-tight">{item.label}</span>
                      {/* Badge (e.g. unread count for Messages) */}
                      {(() => {
                        const badge = item.id === "messages" && totalUnread > 0 ? totalUnread : item.badge;
                        return badge ? (
                          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                            {badge}
                          </span>
                        ) : null;
                      })()}
                      {hasChildren && (
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            isExpanded && "rotate-180"
                          )}
                        />
                      )}
                      {/* Star icon for favoriting */}
                      {showStarInFavorites && (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                          className="p-0.5 rounded transition-colors hover:bg-muted-foreground/10 shrink-0"
                          title="Remove from favorites"
                        >
                          <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                        </button>
                      )}
                      {showStarInOther && (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                          className={cn(
                            "p-0.5 rounded transition-all shrink-0",
                            starred
                              ? "opacity-100"
                              : "opacity-0 group-hover/item:opacity-100",
                            "hover:bg-muted-foreground/10"
                          )}
                          title={starred ? "Remove from favorites" : "Add to favorites"}
                        >
                          <Star
                            className={cn(
                              "h-3.5 w-3.5",
                              starred
                                ? "fill-warning text-warning"
                                : "text-muted-foreground/50"
                            )}
                          />
                        </button>
                      )}
                    </button>

                    {/* Children (sub-items) */}
                    {hasChildren && isExpanded && (
                      <div className="ml-5 mt-1 space-y-1 border-l border-border/60 pl-3">
                        {item.children!.map((child) => {
                          const childActive = isActive(child.path);
                          return (
                            <div key={child.id} className="relative">
                              {childActive && (
                                <span
                                  aria-hidden
                                  className="pointer-events-none absolute -left-[13px] top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-sm bg-primary"
                                />
                              )}
                              <button
                                onClick={() => handleChildClick(child)}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors duration-150 w-full tracking-tight",
                                  childActive
                                    ? "bg-primary/[0.06] text-foreground font-semibold"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                )}
                              >
                                <DynamicIcon
                                  name={child.icon}
                                  className={cn(
                                    "h-4 w-4 transition-colors duration-200",
                                    childActive && "text-primary"
                                  )}
                                />
                                {child.label}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        );
        })}
      </nav>

      {/* "Finish setup" checklist — only renders when there are pending items */}
      <SetupChecklist />

      {/* Settings & User Section */}
      <div className="border-t border-border/40 p-3 space-y-2 shrink-0">
        {/* Settings Link */}
        <div className="relative">
          {isActive("/settings") && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-sm bg-primary"
            />
          )}
          <button
            onClick={() => navigate("/settings")}
            className={cn(
              "flex items-center gap-3 w-full pl-4 pr-3 py-2 rounded-lg transition-colors duration-150 text-[13px] font-medium tracking-tight",
              isActive("/settings")
                ? "bg-primary/[0.06] text-foreground font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <Settings className={cn("h-4 w-4", isActive("/settings") && "text-primary")} />
            My Settings
          </button>
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-card/60 border border-border/40">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="bg-primary text-primary-foreground font-display font-semibold text-xs tracking-tight">
              {initials || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <button
              onClick={onEditProfile}
              className="flex items-center gap-1 text-[13px] font-medium text-foreground hover:opacity-70 transition-opacity group w-full text-left"
            >
              <span className="truncate">{fullName || "Set your name"}</span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={onLogout}
          >
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
